// WatchlistManager.jsx
// Manage watchlists — create, edit, delete, add/remove stocks
// Set default watchlist — used by charts on startup

import React, { useState, useEffect } from 'react';
import axios from 'axios';

import API_BASE from '../config';
import { api } from '../api';

function WatchlistManager() {
  const [watchlists, setWatchlists]         = useState([]);
  const [selectedId, setSelectedId]         = useState(null);
  const [selectedStocks, setSelectedStocks] = useState([]);
  const [newWLName, setNewWLName]           = useState('');
  const [newWLDesc, setNewWLDesc]           = useState('');
  const [newWLDefault, setNewWLDefault]     = useState(false);
  const [addSymbol, setAddSymbol]           = useState('');
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [success, setSuccess]               = useState(null);

  // ============================================================
  // LOAD DATA
  // ============================================================
  useEffect(() => {
    loadWatchlists();
  }, []);

  useEffect(() => {
    if (selectedId) loadWatchlistStocks(selectedId);
  }, [selectedId]);

  const loadWatchlists = async () => {
    try {
      const res = await api.get(`/watchlists`);
      setWatchlists(res.data);
      if (res.data.length > 0 && !selectedId) {
        setSelectedId(res.data[0].id);
      }
    } catch (err) {
      setError('Failed to load watchlists');
    }
  };

  const loadWatchlistStocks = async (id) => {
    try {
      const res = await api.get(`/watchlists/${id}/stocks`);
      setSelectedStocks(res.data);
    } catch (err) {
      setError('Failed to load stocks');
    }
  };

  // ============================================================
  // WATCHLIST ACTIONS
  // ============================================================
  const createWatchlist = async () => {
    if (!newWLName.trim()) return;
    setLoading(true);
    try {
      await api.post(`/watchlists`, null, {
        params: {
          name:        newWLName,
          description: newWLDesc,
          is_default:  newWLDefault,
        }
      });
      setNewWLName('');
      setNewWLDesc('');
      setNewWLDefault(false);
      setSuccess('Watchlist created');
      await loadWatchlists();
    } catch (err) {
      setError('Failed to create watchlist');
    }
    setLoading(false);
  };

  const deleteWatchlist = async (id) => {
    if (!window.confirm('Delete this watchlist?')) return;
    try {
      await api.delete(`/watchlists/${id}`);
      setSelectedId(null);
      setSelectedStocks([]);
      await loadWatchlists();
    } catch (err) {
      setError('Failed to delete watchlist');
    }
  };

  const setAsDefault = async (wl) => {
    try {
      await api.put(
        `/watchlists/${wl.id}/set-default`
      );
      setSuccess(`"${wl.name}" set as default watchlist`);
      await loadWatchlists();
    } catch (err) {
      setError('Failed to set default watchlist');
    }
  };

  const toggleWebSocket = async (wl) => {
    try {
      await api.put(`/watchlists/${wl.id}`, null, {
        params: { enable_websocket: !wl.enable_websocket }
      });
      await loadWatchlists();
    } catch (err) {
      setError('Failed to update watchlist');
    }
  };

  const toggleDownload = async (wl) => {
    try {
      await api.put(`/watchlists/${wl.id}`, null, {
        params: { enable_download: !wl.enable_download }
      });
      await loadWatchlists();
    } catch (err) {
      setError('Failed to update watchlist');
    }
  };

  // ============================================================
  // STOCK ACTIONS
  // ============================================================
  const addStock = async () => {
    if (!addSymbol.trim() || !selectedId) return;
    try {
      await api.post(
        `/watchlists/${selectedId}/stocks`,
        null,
        { params: { symbol: addSymbol.toUpperCase() } }
      );
      setAddSymbol('');
      await loadWatchlistStocks(selectedId);
    } catch (err) {
      setError('Failed to add stock');
    }
  };

  const removeStock = async (symbol) => {
    try {
      await api.delete(
        `/watchlists/${selectedId}/stocks/${symbol}`
      );
      await loadWatchlistStocks(selectedId);
    } catch (err) {
      setError('Failed to remove stock');
    }
  };

  // ============================================================
  // RENDER
  // ============================================================
  const selectedWL = watchlists.find((w) => w.id === selectedId);

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Watchlist Manager</h3>

      {/* Messages */}
      {error && (
        <div style={styles.error}>
          {error}
          <button onClick={() => setError(null)} style={styles.closeBtn}>×</button>
        </div>
      )}
      {success && (
        <div style={styles.success}>
          {success}
          <button onClick={() => setSuccess(null)} style={styles.closeBtn}>×</button>
        </div>
      )}

      {/* Create new watchlist */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>New Watchlist</div>
        <input
          style={styles.input}
          placeholder="Name"
          value={newWLName}
          onChange={(e) => setNewWLName(e.target.value)}
        />
        <input
          style={styles.input}
          placeholder="Description (optional)"
          value={newWLDesc}
          onChange={(e) => setNewWLDesc(e.target.value)}
        />
        {/* Default checkbox */}
        <label style={styles.checkLabel}>
          <input
            type="checkbox"
            checked={newWLDefault}
            onChange={(e) => setNewWLDefault(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          Set as default watchlist
        </label>
        <button
          style={styles.btnPrimary}
          onClick={createWatchlist}
          disabled={loading}
        >
          Create
        </button>
      </div>

      {/* Watchlist list */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Your Watchlists</div>
        {watchlists.length === 0 && (
          <div style={styles.empty}>No watchlists yet</div>
        )}
        {watchlists.map((wl) => (
          <div
            key={wl.id}
            style={{
              ...styles.wlRow,
              ...(selectedId === wl.id ? styles.wlRowActive : {})
            }}
            onClick={() => setSelectedId(wl.id)}
          >
            {/* Default star indicator */}
            <span
              style={{
                ...styles.starIcon,
                color: wl.is_default ? '#f59e0b' : '#ccc',
              }}
              title={wl.is_default ? 'Default watchlist' : ''}
            >
              ⭐
            </span>

            <span style={styles.wlName}>{wl.name}</span>

            <div style={styles.wlActions}>
              {/* Set as default button */}
              {!wl.is_default && (
                <button
                  style={styles.btnSetDefault}
                  onClick={(e) => { e.stopPropagation(); setAsDefault(wl); }}
                  title="Set as default watchlist"
                >
                  Set Default
                </button>
              )}
              {wl.is_default && (
                <span style={styles.defaultBadge}>Default</span>
              )}

              {/* WebSocket toggle */}
              <button
                style={{
                  ...styles.badge,
                  ...(wl.enable_websocket ? styles.badgeOn : styles.badgeOff)
                }}
                onClick={(e) => { e.stopPropagation(); toggleWebSocket(wl); }}
                title="Toggle WebSocket feed"
              >
                WS
              </button>

              {/* Download toggle */}
              <button
                style={{
                  ...styles.badge,
                  ...(wl.enable_download ? styles.badgeOn : styles.badgeOff)
                }}
                onClick={(e) => { e.stopPropagation(); toggleDownload(wl); }}
                title="Toggle REST API download"
              >
                DL
              </button>

              {/* Delete */}
              <button
                style={styles.btnDelete}
                onClick={(e) => { e.stopPropagation(); deleteWatchlist(wl.id); }}
                title="Delete watchlist"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Stocks in selected watchlist */}
      {selectedWL && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            Stocks in "{selectedWL.name}"
          </div>

          {/* Add stock */}
          <div style={styles.addRow}>
            <input
              style={{ ...styles.input, flex: 1, marginBottom: 0 }}
              placeholder="Symbol e.g. RELIND"
              value={addSymbol}
              onChange={(e) => setAddSymbol(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addStock()}
            />
            <button style={styles.btnPrimary} onClick={addStock}>
              Add
            </button>
          </div>

          {/* Stock list */}
          {selectedStocks.length === 0 && (
            <div style={styles.empty}>No stocks in this watchlist</div>
          )}
          {selectedStocks.map((stock) => (
            <div key={stock.symbol} style={styles.stockRow}>
              <span style={styles.symbol}>{stock.symbol}</span>
              <span style={styles.stockName}>{stock.name}</span>
              <button
                style={styles.btnDelete}
                onClick={() => removeStock(stock.symbol)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: {
    padding:    '16px',
    overflowY:  'auto',
    height:     '100%',
    background: '#f5f5f5',
    color:      '#131722',
  },
  title: {
    fontSize:     '15px',
    fontWeight:   'bold',
    marginBottom: '12px',
    color:        '#131722',
  },
  card: {
    backgroundColor: '#ffffff',
    border:          '1px solid #e0e0e0',
    borderRadius:    '8px',
    padding:         '16px',
    marginBottom:    '16px',
    boxShadow:       '0 1px 3px rgba(0,0,0,0.08)',
  },
  cardTitle: {
    color:         '#666',
    fontSize:      '11px',
    textTransform: 'uppercase',
    marginBottom:  '8px',
    letterSpacing: '0.5px',
    fontWeight:    'bold',
  },
  input: {
    width:           '100%',
    padding:         '6px 8px',
    backgroundColor: '#ffffff',
    color:           '#131722',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    fontSize:        '13px',
    marginBottom:    '6px',
    boxSizing:       'border-box',
  },
  checkLabel: {
    display:      'flex',
    alignItems:   'center',
    fontSize:     '13px',
    color:        '#444',
    marginBottom: '10px',
    cursor:       'pointer',
  },
  btnPrimary: {
    padding:         '6px 12px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontSize:        '12px',
  },
  btnDelete: {
    padding:         '2px 6px',
    backgroundColor: 'transparent',
    color:           '#999',
    border:          'none',
    cursor:          'pointer',
    fontSize:        '16px',
  },
  btnSetDefault: {
    padding:         '2px 8px',
    backgroundColor: '#fff8e1',
    color:           '#f59e0b',
    border:          '1px solid #f59e0b',
    borderRadius:    '3px',
    cursor:          'pointer',
    fontSize:        '10px',
    fontWeight:      'bold',
  },
  defaultBadge: {
    padding:         '2px 8px',
    backgroundColor: '#f59e0b',
    color:           '#fff',
    borderRadius:    '3px',
    fontSize:        '10px',
    fontWeight:      'bold',
  },
  wlRow: {
    display:        'flex',
    alignItems:     'center',
    gap:            '6px',
    padding:        '6px 8px',
    borderRadius:   '4px',
    cursor:         'pointer',
    marginBottom:   '4px',
  },
  wlRowActive: {
    backgroundColor: '#e8f0fe',
  },
  starIcon: {
    fontSize:   '12px',
    flexShrink: 0,
  },
  wlName: {
    color:    '#131722',
    fontSize: '13px',
    flex:     1,
  },
  wlActions: {
    display:    'flex',
    alignItems: 'center',
    gap:        '4px',
  },
  badge: {
    padding:      '2px 6px',
    borderRadius: '3px',
    fontSize:     '10px',
    cursor:       'pointer',
    border:       'none',
    fontWeight:   'bold',
  },
  badgeOn: {
    backgroundColor: '#2962ff',
    color:           '#fff',
  },
  badgeOff: {
    backgroundColor: '#e0e0e0',
    color:           '#999',
  },
  stockRow: {
    display:      'flex',
    alignItems:   'center',
    gap:          '8px',
    padding:      '5px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  symbol: {
    color:      '#131722',
    fontSize:   '13px',
    fontWeight: 'bold',
    minWidth:   '80px',
  },
  stockName: {
    color:    '#888',
    fontSize: '12px',
    flex:     1,
  },
  addRow: {
    display:      'flex',
    gap:          '6px',
    marginBottom: '8px',
  },
  empty: {
    color:    '#aaa',
    fontSize: '12px',
    padding:  '8px 0',
  },
  error: {
    backgroundColor: '#fff0f0',
    color:           '#d32f2f',
    padding:         '8px 12px',
    borderRadius:    '4px',
    marginBottom:    '10px',
    fontSize:        '12px',
    display:         'flex',
    justifyContent:  'space-between',
    border:          '1px solid #ffcdd2',
  },
  success: {
    backgroundColor: '#f0fff4',
    color:           '#2e7d32',
    padding:         '8px 12px',
    borderRadius:    '4px',
    marginBottom:    '10px',
    fontSize:        '12px',
    display:         'flex',
    justifyContent:  'space-between',
    border:          '1px solid #c8e6c9',
  },
  closeBtn: {
    background: 'none',
    border:     'none',
    cursor:     'pointer',
    fontSize:   '16px',
    color:      'inherit',
  },
};

export default WatchlistManager;